import React, { useState } from 'react'

import { View, Text, FormFieldGroup, ScreenReaderContent, Checkbox, CheckboxGroup, Alert, Button, IconButton, Spinner, IconXSolid, TextInput } from '@instructure/ui'

import Papa from 'papaparse'

import PropTypes from 'prop-types'

import { toDataURI } from './utils'
import { fetchMembers } from './fetchMembers'

/**
 * Canvas SIS Import export panel (self-contained).
 *
 * Internal state managed here:
 * - alerts: user feedback messages
 * - members: fetched roster cache
 * - sisCourseId: optional target Canvas course id to be placed in CSV first column
 * - isLoading / isWriting: network vs. file generation progress flags
 *
 * CSV shape: [course_id, user_id, role, status]
 * - course_id: optional (input field), blank if not provided
 * - user_id: Canvas login_id
 * - role: always empty, to be filled in by the end user later
 * - status: 'active'
 *
 * @param {{ ltiServer: string, jwt: string }} props
 * @returns {JSX.Element}
 */
function SisImportPanel({ ltiServer, jwt }) {
	// Local state encapsulated within panel
	const [alerts, setAlerts] = useState([])
	const defaultRoles = ['http://purl.imsglobal.org/vocab/lis/v2/membership#Learner']
	const [roles, setRoles] = useState(defaultRoles)
	const [members, setMembers] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isWriting, setIsWriting] = useState(false)
	const [sisCourseId, setSisCourseId] = useState('')

	// Simple unique id for dismissible alerts
	const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36)


	/**
	 * Build SIS CSV string.
	 * Filters members by selected roles and excludes unactivated placeholders.
	 * @param {Array<Object>} membersListArg
	 * @returns {Promise<string>}
	 */
	const sisCsvContent = (membersListArg) => {
		const allowedRoles = roles
		const list = membersListArg ?? members ?? []
		// Include all members (except the activation placeholder)
		const activeMembers = list.filter(user => user.login_id !== "$Canvas.user.loginId")
		const rows = [["course_id", "user_id", "role", "status"],
			...activeMembers.map(member => [
				sisCourseId || '',
				member.sis_source_id,
				'',
				'active'
			])
		]
		return new Promise(resolve => resolve(Papa.unparse(rows)))
	}

	/**
	 * Generate CSV and trigger download.
	 * @param {Array<Object>} membersListArg
	 */
	const sendSisCsv = (membersListArg) => {
		setIsWriting(true)
		sisCsvContent(membersListArg).then(csvContentStr => {
			const encodedUri = encodeURI(toDataURI(csvContentStr))
			const link = document.createElement('a')
			link.setAttribute('href', encodedUri)
			link.setAttribute('download', 'sis_import.csv')
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
		}).finally(() => setIsWriting(false))
	}

	/**
	 * Orchestrate fetch + download when needed.
	 */
	const downloadSis = () => {

		if (members == null) {
			setIsLoading(true)
			fetchMembers(ltiServer, jwt)
				.then(membersResult => {
					setMembers(membersResult)
					sendSisCsv(membersResult)
				})
				.catch(reason => {
					setAlerts(current => current.concat([{ id: uid(), variant: 'error', message: 'Failed to load members: ' + reason.message }]))
				})
				.finally(() => setIsLoading(false))
		} else {
			sendSisCsv(members)
		}
	}

	/**
	 * Render alert messages.
	 * @returns {JSX.Element[]}
	 */
	const renderAlerts = (list) => list && list.map((alert, index) => (
		<Alert
			margin="small"
			key={alert.id ?? index}
			variant={alert.variant}
			renderCloseButtonLabel="Close"
			onDismiss={() => setAlerts(curr => curr.filter(a => (a.id ?? a.message + a.variant) !== (alert.id ?? alert.message + alert.variant)))}
		>
			{alert.message}
		</Alert>
	))

    const isProcessing = isLoading || isWriting

    return (
        <View as="div" padding="small">
			<Alert variant="warning"
                margin="small"
                variantScreenReaderLabel="Warning, ">Role(s) do not currently appear in the CSV export.</Alert>
            {renderAlerts(alerts)}
            <View as="div" padding="medium none">
                <Text color="primary">
                    This allows you to export a list of course members in a CSV format. 
					Assuming it has valid entries in the 'course_id' and 'role' columns, 
					it can be imported into Canvas via a SIS Import.
                </Text>
            </View>
            
			<FormFieldGroup description={<ScreenReaderContent>Limit Result</ScreenReaderContent>}>
				<CheckboxGroup
					name="roles"
					onChange={(value) => setRoles(value)}
					defaultValue={defaultRoles}
					description="Members to export"
				>
					<Checkbox label="Staff" value="http://purl.imsglobal.org/vocab/lis/v2/membership#Instructor" />
					<Checkbox label="Learners" value="http://purl.imsglobal.org/vocab/lis/v2/membership#Learner" />
				</CheckboxGroup>
				         

                <TextInput
                    renderLabel="Target Canvas Course (Optional)"
                    id="sis-course-id"
                    value={sisCourseId}
                    onChange={(e) => setSisCourseId(e.target.value)}
                    placeholder="e.g. 0764205-0eac-46af-be81-014099f7c319"
                    renderAfterInput={sisCourseId ? (
                        <IconButton
                            withBackground={false}
                            withBorder={false}
                            screenReaderLabel="Clear course"
                            onClick={() => setSisCourseId('')}
                            size="small"
                            margin="0 small 0 0"
                        >
                            <IconXSolid />
                        </IconButton>
                    ) : null}
                />
			</FormFieldGroup>

			<Alert key="delete-after-download-sis" variant="info">
				Please ensure you delete the file once you have used it for importing enrolments into Canvas.
			</Alert>

			<View as="div">
				<Button onClick={downloadSis} padding="small" color="primary" interaction={isProcessing ? 'disabled' : 'enabled'}>
					Download
				</Button>
				{isProcessing && <Spinner size="x-small" margin="0 medium" renderTitle="Loading course members" />}
			</View>
		</View>
	)
}

SisImportPanel.propTypes = {
	ltiServer: PropTypes.string.isRequired,
	jwt: PropTypes.string.isRequired
}

export default SisImportPanel
